import React, { useContext, useEffect, useState } from "react";
import SyntaxHighlighter from "react-syntax-highlighter";
import { a11yDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { MdArrowForwardIos, MdArrowBackIosNew } from "react-icons/md";
import { HiOutlineChevronRight } from "react-icons/hi2";

import { AppContext } from "./context";
import toast from "react-hot-toast";

export default function Step() {
  const context = useContext(AppContext);
  const [showExplanation, setShowExplanation] = useState(false);

  const step = context.workshop.steps[context.index - 1];
  const totalSteps = context.workshop.steps.length;

  if (!step) return <div />;

  const handleAnswerSelect = (question, answer) => {
    const updatedUserWorkshop = { ...context.userWorkshop };

    const questions = updatedUserWorkshop.questions || [];
    const questionIndex = questions.findIndex((q) => q.key === question.key);

    const answers = questions[questionIndex]?.answers || [];
    if (answers.length === 0) {
      questions.push({
        key: question.key,
        question: question.question,
        answers: [
          {
            key: answer.key,
            answer: answer.answer,
            user_answer: true,
            correct_answer: answer.isCorrect,
          },
        ],
      });
    } else {
      const answerIndex = answers.findIndex((a) => a.key === answer.key);

      if (answerIndex >= 0) {
        questions[questionIndex].answers[answerIndex] = {
          ...questions[questionIndex].answers[answerIndex],
          user_answer: !questions[questionIndex].answers[answerIndex].user_answer,
        };
      } else {
        questions[questionIndex].answers.push({
          key: answer.key,
          answer: answer.answer,
          user_answer: true,
          correct_answer: answer.isCorrect,
        });
      }
    }
    context.setUserWorkshop({ ...updatedUserWorkshop, questions });
  };

  const validate = () => {
    context.updateUserWorkshop();

    const questions = context.userWorkshop?.questions || [];
    const stepKeys = step.elements.filter((e) => e.kind === "question").map((e) => e.value.key);
    const questionsInStep = questions.filter((q) => stepKeys.includes(q.key));
    const errors = questionsInStep.filter((q) => {
      return q.answers.some((a) => a.correct_answer !== a.user_answer);
    });
    if (errors.length > 0) {
      toast.error("Please answer all questions correctly");
      setShowExplanation(true);
      return false;
    }
    return true;
  };

  const Content = () => {
    const elements = [];

    let hasImage = false;
    let imageElement = null;
    let textElements = [];

    for (let i = 0; i < step.elements.length; i++) {
      const element = step.elements[i];

      if (element.kind === "image" && element.value) {
        hasImage = true;
        imageElement = (
          <div className="w-full max-w-md mx-auto" key={`image-${i}`}>
            <img src={element.value?.rawBody || element.value} className="w-full rounded-xl shadow-sm" />
          </div>
        );
      } else if (element.kind === "text") {
        textElements.push(
          <div className="max-w-3xl mx-auto mb-6" key={`text-${i}`}>
            {i === 0 && (
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 pb-3 border-b border-gray-100">
                <span className="text-blue-600 mr-2">{context.index}.</span>
                {context.workshop.steps[context.index - 1]?.title}
              </h2>
            )}
            <div className="rich-text text-sm md:text-base lg:text-lg text-gray-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: element.value }} />
          </div>,
        );
      } else if (element.kind === "code") {
        textElements.push(
          <div className="max-w-3xl mx-auto mb-6 rounded-xl overflow-hidden shadow-sm" key={`code-${i}`}>
            <SyntaxHighlighter language="javascript" showLineNumbers={true} showInlineLineNumbers={true} style={a11yDark} customStyle={{ borderRadius: "0.75rem", fontSize: "0.8rem", padding: "1.25rem" }}>
              {element.value}
            </SyntaxHighlighter>
          </div>,
        );
      } else if (element.kind === "video") {
        textElements.push(
          <div className="max-w-3xl mx-auto mb-6 rounded-xl overflow-hidden shadow-sm" key={`video-${i}`}>
            <video src={element.value} frameBorder="0" className="h-full w-full rounded-xl aspect-video" controls allowFullScreen></video>
          </div>,
        );
      } else if (element.kind === "question") {
        textElements.push(
          <div className="max-w-3xl mx-auto mb-6 bg-blue-50/50 border border-blue-100 rounded-xl p-6" key={`question-${element.value.question}`}>
            <div className="text-base font-semibold text-gray-900 mb-4 flex items-start gap-2">
              <span className="text-blue-600 text-lg mt-0.5">?</span>
              <span>{element.value.question}</span>
            </div>
            <div className="flex flex-col gap-2.5">
              {element.value.answers.map((answer) => {
                const question = context.userWorkshop?.questions?.find((question) => question.key === element.value.key);
                const isSelected = question?.answers.some((a) => a.key === answer.key && a.user_answer === true);
                return (
                  <label
                    key={answer.key}
                    className={`flex items-center gap-3 p-3.5 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                      isSelected
                        ? "border-blue-500 bg-blue-50 shadow-sm"
                        : "border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/30"
                    }`}
                    onClick={() => handleAnswerSelect(element.value, answer)}>
                    <div className={`w-5 h-5 rounded-md border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                      isSelected ? "border-blue-500 bg-blue-500" : "border-gray-300"
                    }`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-sm ${isSelected ? "font-medium text-gray-900" : "text-gray-700"}`}>{answer.answer}</span>
                    {showExplanation && answer.explanation && (
                      <span className="ml-auto text-xs text-red-500 italic">{answer.explanation}</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>,
        );
      }
    }

    if (hasImage) {
      elements.push(
        <div key="image-layout" className="flex flex-col lg:flex-row items-start gap-8 max-w-5xl mx-auto">
          {imageElement}
          <div className="flex-1 min-w-0">{textElements}</div>
        </div>,
      );
    } else {
      elements.push(...textElements);
    }

    return <div className="space-y-2">{elements}</div>;
  };

  const Footer = () => {
    useEffect(() => {
      function handleSpaceBar(evt) {
        if (document.activeElement.tagName === "TEXTAREA" || document.activeElement.tagName === "INPUT") return;
        if (evt.key == " " || evt.code == "Space" || evt.code == 32) onContinue();
      }
      document.body.addEventListener("keyup", handleSpaceBar);
      return () => document.body.removeEventListener("keyup", handleSpaceBar);
    }, []);

    const onContinue = () => {
      if (validate()) {
        setShowExplanation(false);
        context.next();
      }
    };

    return (
      <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-lg z-50">
        <div className="flex items-center justify-between max-w-5xl mx-auto px-6 py-3">
          {/* Step counter */}
          <div className="text-sm text-gray-500 font-medium hidden sm:block">
            Step {context.index} of {totalSteps}
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center gap-3 mx-auto sm:mx-0">
            {context.index >= 2 && (
              <button
                onClick={() => context.setIndex(context.index - 1)}
                className="group flex gap-2 items-center bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-5 py-2.5 rounded-xl transition-colors duration-200">
                <MdArrowBackIosNew className="text-sm group-hover:-translate-x-1 transition-transform" />
                <span className="hidden sm:inline">Previous</span>
              </button>
            )}
            <button
              className="group flex gap-2 items-center bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2.5 rounded-xl transition-colors duration-200 shadow-sm"
              onClick={onContinue}>
              {context.index === totalSteps ? "Finish" : "Continue"}
              <MdArrowForwardIos className="text-sm group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Keyboard hint */}
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-400">
            <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono border border-gray-200">Space</kbd>
            <span>to continue</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="w-full pb-4">
        <Content />
      </div>
      <Footer />
    </>
  );
}
